import { Component, OnInit, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TablePagination,
  TableSearchEvent,
  TableSort,
  TableDateRangeEvent,
} from "../../../../Common/Components/Table/Type/TableType";
import { UserListItem } from "../../Dto/UserModel";
import { UserEffects } from "../../State/UserEffect";
import { UserState } from "../../State/UserState";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-user-home",
  standalone: true,
  imports: [AppTableComponent],
  templateUrl: "./UserHomePage.html",
  styleUrl: "./UserHomePage.scss",
})
export class UserHomePage implements OnInit {
  private readonly userEffects = inject(UserEffects);
  private readonly router = inject(Router);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly toastEffects = inject(ToastEffects);
  readonly userState = inject(UserState);

  usersColumns: TableColumn<UserListItem>[] = [
    { key: "id", header: "ID", sortable: true, width: "10%", visibleByDefault: false },
    { key: "name", header: "Name", sortable: true, width: "18%" },
    { key: "email", header: "Email", sortable: true, width: "22%" },
    { key: "role.name", header: "Role", sortable: true, width: "12%" },
    {
      key: "role.isSystem",
      header: "System Role",
      sortable: true,
      width: "12%",
      cellTemplate: (item) =>
        (() => {
          if (item.role?.isSystem) {
            return "Yes";
          }
          return "No";
        })(),
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      width: "20%",
      cellTemplate: (item) => formatDateNumeric(item.createdAt),
    },
  ];

  get usersConfig(): TableConfig<UserListItem> {
    return {
      loading: this.userState.loading(),
      emptyMessage: "No users found",
      showActions: true,
      showSearch: true,
      searchPlaceholder: "Search users...",
      showCreate: true,
      createButtonLabel: "Create User",
      createButtonIcon: "person_add",
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by creation date",
      onCreate: () => this.createUser(),
      actionButtons: [
        {
          icon: "edit",
          label: "Edit",
          handler: (item: UserListItem) => this.editUser(item),
          visible: () => true,
        },
        {
          icon: "delete",
          label: "Delete",
          handler: (item: UserListItem) => this.deleteUser(item),
          visible: () => true,
        },
      ],
    };
  }

  get usersPagination(): TablePagination {
    return this.userState.pagination();
  }

  ngOnInit(): void {
    this.userEffects.searchUsers().catch(() => undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    // Removed logging
  }

  onPageChange(event: { page: number; search?: string }): void {
    const currentSearchParams = this.userState.searchParams();
    this.userEffects.searchUsers({ ...currentSearchParams, page: event.page, search: event.search }).catch(() => undefined);
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    const currentSearchParams = this.userState.searchParams();
    this.userEffects
      .searchUsers({ ...currentSearchParams, page: 1, limit: event.limit, search: event.search })
      .catch(() => undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    // Removed logging
  }

  onSearchChange(event: TableSearchEvent): void {
    this.userEffects.searchUsers({ search: event.term, page: event.page }).catch(() => undefined);
  }

  onDateRangeChange(event: TableDateRangeEvent): void {
    const currentSearchParams = this.userState.searchParams();
    this.userEffects
      .searchUsers({
        ...currentSearchParams,
        page: event.page,
        createdAtFrom: event.dateRange.from,
        createdAtTo: event.dateRange.to,
      })
      .catch(() => undefined);
  }

  createUser(): void {
    this.router.navigate(["/users/create"]).catch(console.error);
  }

  editUser(user: UserListItem): void {
    this.router.navigate(["/users/edit", user.id], { state: { user } }).catch(console.error);
  }

  deleteUser(user: UserListItem): void {
    this.confirmModalEffects.open({
      title: "Delete user?",
      message: `This will permanently delete ${user.name} and remove their access.`,
      confirmText: "Delete user",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        try {
          await this.userEffects.deleteManageUser(user.id);
          this.toastEffects.show("User deleted", "success");
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, "Failed to delete user"), "error");
        }
      },
    });
  }
}
